import axios from "axios";
import { API_URL } from "helper/Constants";
import { ENCRYPTION_OF_DATA } from "helper/Functions";
import Cookies from "js-cookie";

class TransferService {
  constructor(baseURL) {
    //console.log("Transfer BASE URL : ", baseURL)
    this.api = axios.create({
      baseURL: baseURL || API_URL, // Ensure this is your correct backend URL
    });

    this.lastModified = null; // Store Last-Modified timestamp

    // Add an interceptor to handle token refresh
    this.api.interceptors.request.use(
      async (config) => {
        const token = Cookies.get("token");
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        if (config.data) {
          config.headers["Content-Type"] = `application/json`;
          config.data = await ENCRYPTION_OF_DATA(config.data);// Send the entire encrypted object
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add a response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response && (error.response.status === 401 && error.response.data.message !== "Invalid refresh token")) {
          try {
            const newToken = await this.refreshToken();
            if (newToken) {
              // Update the token in the request header
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            // console.log(refreshError,'refreshError')
            window?.location?.reload()
            return Promise.reject(refreshError);
          }

        }

        return Promise.reject(error);
      }
    );
  }

  async refreshToken() {
    try {
      const response = await this.api.post(
        "/auth/refresh-token",
        {},
        {
          withCredentials: true, // Optional: Use this if you still want to send cookies
        }
      );
      const newAccessToken = response.data.accessToken;
      Cookies.set("token", newAccessToken, {
        secure: true, // Only transmit over HTTPS in production
        sameSite: "None", // Required for cross-origin
      }); // Save the new token
      return newAccessToken;
    } catch (error) {
      Cookies.remove("user", { secure: true, sameSite: "None" });
      Cookies.remove("token", { secure: true, sameSite: "None" });
      Cookies.remove("refreshToken", { secure: true, sameSite: "None" });
      window?.location?.reload()
      console.error("Failed to refresh token:", error);
      return null;
    }
  }

  async makeTransfer(senderId, receiverId, amount, type, note, transactionId) {
    try {
      const token = Cookies.get("token");

      const response = await this.api.post(
        "/tr/transfer",
        {
          senderId: senderId,
          receiverId: receiverId,
          amount,
          type,
          note,
          transaction_id: transactionId, // Include transaction_id in the payload
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return {
        success: true,
        message: response.data.message,
        transfer: response.data.data.transfer,
        updatedSender: response.data.updatedSender,
        updatedReceiver: response.data.updatedReceiver,
      };
    } catch (error) {
      console.error("Erreur lors de la création du transfert :", error);

      if (error.response) {
        return {
          success: false,
          message: error.response.data.message || "Une erreur est survenue lors de la création du transfert",
          status: error.response.status,
        };
      } else {
        return {
          success: false,
          message: "Erreur réseau ou le serveur est inaccessible.",
          status: 500,
        };
      }
    }
  }

  async getUserInfo(username) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.get(`/auth/pages/User/${username}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        user: response.data.user,
      };
    } catch (error) {
      console.error("Erreur lors de la récupération des informations de l'utilisateur :", error);
      return {
        success: false,
        message: error.response ? error.response.data.message : "Une erreur est survenue lors de la récupération des informations de l'utilisateur",
      };
    }
  }

  async getTransferHistory(username, date) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.get(`/tr/transfer-history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { username, startDate: date["startDate"], endDate: date["endDate"] },
      });

      return {
        success: true,
        transferHistory: response.data.transferHistory,
      };
    } catch (error) {
      console.error("Error fetching transfer history:", error);
      return {
        success: false,
        message: error.response?.data.message || "An error occurred while fetching transfer history",
        status: error.response?.status || 500,
      };
    }
  }
  async calculateMoneyDetails() {
    try {
      const token = Cookies.get("token");
      const response = await this.api.get(`/tr/calculate-money-details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        success: true,
        transfers: response.data.transfers,
      };
    } catch (error) {
      console.error("Error fetching money details:", error);
      return {
        success: false,
        message: error.response?.data.message || "An error occurred while fetching money details",
        status: error.response?.status || 500,
      };
    }
  }

  async getAllCreaterUsers(creatorId) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.get(`/auth/getAllUsersByCreatorId?createrid=${creatorId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return {
        success: true,
        status: response.status,
        users: response.data.users,
      };
    } catch (error) {
      console.error("Erreur lors de la récupération de tous les utilisateurs :", error);

      if (error.response) {
        return {
          success: false,
          status: error.response.status,
          message: error.response.data.message || "Une erreur est survenue lors de la récupération des utilisateurs",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }

  async getAllTransferReports(query) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.get(`/tr/transfer-report?startDate=${query?.startDate}&endDate=${query?.endDate}&userId=${query?.userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return {
        success: true,
        status: response.status,
        reports: response.data,
      };
    } catch (error) {
      console.error("Erreur lors de la récupération de tous les utilisateurs :", error);

      if (error.response) {
        return {
          success: false,
          status: error.response.status,
          message: error.response.data.message || "Une erreur est survenue lors de la récupération des utilisateurs",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }

  async getAllCasinoBets(query) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.get(`/tr/casino-bets?startDate=${query?.startDate}&endDate=${query?.endDate}${query.query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return {
        success: true,
        status: response.status,
        bets: response.data.data,
      };
    } catch (error) {
      console.error("Erreur lors de la récupération de tous les utilisateurs :", error);
      if (error.response) {
        return {
          success: false,
          status: error.response.status,
          message: error.response.data.message || "Une erreur est survenue lors de la récupération des utilisateurs",
        };
      } else {
        return {
          success: false,
          status: 500,
          message: "Network error or server is unreachable.",
        };
      }
    }
  }
}

export default TransferService;
